import type { PackOpenerOptions } from 'pack-opener-js';
import { defaultPresetFor } from './presets';
import { DEFAULT_SOUND, isOpenerVariant, type AnimationConfig } from './types';
import { WEB_BASE, WEB_BASE_CAROUSEL } from './webBase';

/** One line of the carousel's story: `YEAR` / `2000`. */
export interface CardFact {
  label: string;
  value: string;
}

/** The tier the carousel shouts across its banner, in the tier's colour. */
export interface CardBadge {
  label: string;
  /** `#hex` or `rgb()` — Pixi parses nothing else. */
  color: string;
}

export interface OpenerContext {
  /** Pack artwork — the one thing the scene cannot run without. */
  packUrl: string;
  /** Card artwork; the scene waits for it and finishes without it after a timeout. */
  cardUrl?: string;
  /** What the carousel tells before the card is shown — any the host has; empty tells nothing. */
  facts?: CardFact[];
  /**
   * The pull's tier, for a tier worth naming: the carousel runs it on its
   * banner, slice and burst write it behind the landed card when `badgeWord`.
   */
  badge?: CardBadge;
  /**
   * The rarity word behind the landed card (pack-opener-js ≥ 0.5.12) — the
   * operator's switch, but only the host knows whether its template draws
   * it. The host's own — no knob may set it.
   */
  badgeWord?: boolean;
  /** The stand under the revealed card; without one the scene draws the one baked into the library. */
  pedestalUrl?: string;
  /** Rarity halo colour as `#hex` or `rgb()` — Pixi parses nothing else. */
  glow: string;
  /**
   * Bands of the stage, in css px, that the host draws over (a button under
   * the pack, a title above the card). The scene keeps the pack and the card
   * clear of them. The host's own — no knob may set them.
   */
  reserve?: { top?: number; bottom?: number };
  /**
   * Where the host's own winner layout would put the card and its plinth,
   * in css px on the stage: the scene rests its card exactly there, so the
   * winner chrome arrives around a card that already sits where its own
   * would have. The host's own — no knob may set it (pack-opener-js ≥ 0.4.1).
   */
  rest?: {
    card: { left: number; top: number; width: number; height: number };
    pedestal: { left: number; width: number; bottom: number };
  };
  /**
   * The player's side of the ceremony's sound: the library plays the score
   * itself (pack-opener-js ≥ 0.5.8) on the operator's `sound` settings; the
   * host adds the player's own mute and the pull's badge, which decides how
   * hard the landing hits. The host's own — no knob may set them.
   */
  sound?: {
    muted?: boolean;
    badge?: string | null;
  };
}

type Plain = Record<string, unknown>;

/** How long the scene keeps turning the card for artwork that has not come. */
export const CARD_ARTWORK_TIMEOUT_MS = 15000;

const isPlain = (value: unknown): value is Plain =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** Right-hand values win; nested plain objects merge, everything else replaces. */
function deepMerge(base: Plain, patch: Plain): Plain {
  const out: Plain = { ...base };
  for (const [key, value] of Object.entries(patch)) {
    if (value === undefined) continue;
    const current = out[key];
    out[key] = isPlain(current) && isPlain(value) ? deepMerge(current, value) : value;
  }
  return out;
}

/**
 * Builds the options pack-opener-js runs on: the chosen variant and preset,
 * the artwork, the web base (`WEB_BASE`, or `WEB_BASE_CAROUSEL` for the
 * carousel, which lays its ring out on its own terms), the operator's delta
 * on top of it — and the host's keys forced last. `background` stays
 * transparent so the operator's page shows through, `glow` is the card's
 * rarity colour, `layout.stage` is whatever the host reserved for its own UI,
 * the card turns where the pack was and the beam runs as a ribbon; no knob
 * may override any of those.
 *
 * Throws for `slot`: that mechanic is not rendered by the library, so a caller
 * has to branch before getting here.
 */
export function toPackOpenerOptions(config: AnimationConfig, ctx: OpenerContext): PackOpenerOptions {
  if (!isOpenerVariant(config.variant)) {
    throw new Error('toPackOpenerOptions: the slot lane is not a pack-opener-js mechanic');
  }

  const base: Plain = deepMerge(
    {
      variant: config.variant,
      preset: config.preset ?? defaultPresetFor(config.variant),
      assets: {
        pack: { url: ctx.packUrl },
        // The card's artwork arrives once the purchase has settled, mid-ceremony;
        // the scene keeps turning the card until it does. The library's own
        // patience (4 s) is a phone's: past it the card unveils blank, which is
        // worse than a longer turn on a slow round trip
        card: {
          ...(ctx.cardUrl ? { url: ctx.cardUrl } : {}),
          timeoutMs: CARD_ARTWORK_TIMEOUT_MS,
          // The carousel's story, and the tier slice and burst may write behind the card
          ...(ctx.facts ? { facts: ctx.facts } : {}),
          ...(ctx.badge ? { badge: ctx.badge } : {}),
        },
        ...(ctx.pedestalUrl ? { pedestal: { url: ctx.pedestalUrl } } : {}),
      },
    },
    (config.variant === 'carousel' ? WEB_BASE_CAROUSEL : WEB_BASE) as unknown as Plain,
  );

  const merged = deepMerge(base, (config.options ?? {}) as Plain);
  const theme = isPlain(merged['theme']) ? merged['theme'] : {};
  merged['theme'] = { ...theme, background: 'transparent', glow: ctx.glow };

  // The beam as one soft streak: the segmented one reads as a row of dots on a
  // desktop stage. The native app keeps the library's default look, so this is
  // the web host's call, not a knob (pack-opener-js ≥ 0.4.1; ignored before).
  // The rarity word is the host's call too
  const motion = isPlain(merged['motion']) ? merged['motion'] : {};
  const reveal = isPlain(motion['reveal']) ? motion['reveal'] : {};
  merged['motion'] = {
    ...motion,
    reveal: { ...reveal, beamStyle: 'ribbon', badgeWord: ctx.badgeWord ?? false },
  };

  // The card turns where the pack was and lifts onto its stand only as the
  // artwork is unveiled (0.4.1 as well): the pack and the turning card sit
  // low in their zone, the finished card ends up on the stand. And the bands
  // the host draws over — the winner's title and panel — are kept clear
  const layout = isPlain(merged['layout']) ? merged['layout'] : {};
  const card = isPlain(layout['card']) ? layout['card'] : {};
  merged['layout'] = {
    ...layout,
    card: { ...card, spinAt: 'pack' },
    ...(ctx.reserve
      ? { stage: { reserveTop: ctx.reserve.top ?? 0, reserveBottom: ctx.reserve.bottom ?? 0 } }
      : {}),
  };
  if (ctx.rest) merged['rest'] = ctx.rest;

  // The score is the library's own: whether and how loud is the operator's
  // setting, the mute is the player's, the badge is the pull's — set last, so
  // no knob in the operator's delta can turn the sound on or off
  const sound = config.sound ?? DEFAULT_SOUND;
  merged['sound'] = {
    enabled: sound.enabled,
    volume: sound.volume,
    muted: ctx.sound?.muted ?? false,
    badge: ctx.sound?.badge ?? null,
  };

  return merged as unknown as PackOpenerOptions;
}
